/** Language picker (components/LanguagePicker/spec.md): the first-visit list and the in-session
 *  popover. */
import classNames from 'classnames'
import {Icon} from '@ui/Icon/icon'
import {LANGUAGES, findLanguage} from '@product/quies/data/languages'
import {voicesFor, type VoiceRoster} from '@product/quies/utils/voiceChoice'
import {VoiceList} from '@product/quies/components/VoicePicker/VoicePicker'
import {PointOfLight} from '@product/quies/components/PointOfLight/PointOfLight'
import {usePopover} from '@product/quies/hooks/usePopover'
import {useInterfaceStrings} from '@product/quies/i18n/catalogue'
import styles from './LanguagePicker.module.scss'
import button from '@product/quies/styles/button.module.scss'

interface FirstVisitProps {
    language: string | null
    onLanguageChange: (code: string) => void
    voiceId: string | null
    onVoiceChange: (voiceId: string) => void
    /** The live voice catalogue (session loader). */
    voiceRoster: VoiceRoster
    onBegin: () => void
    beginDisabled?: boolean
    /** The rule of the first session, told under Begin before it applies (a guest's visit). */
    note?: string
}

/** Selecting a language never starts the session by itself (decisions.md §11). */
export function LanguagePickerFirstVisit({language, onLanguageChange, voiceId, onVoiceChange, voiceRoster, onBegin, beginDisabled, note}: FirstVisitProps) {
    const {t} = useInterfaceStrings()
    const voices = voicesFor(voiceRoster, language)
    return (
        <div className={styles.firstVisit}>
            <PointOfLight size="hero" />
            <fieldset className={styles.grid}>
                <legend className="sr-only">{t.session.chooseLanguage}</legend>
                {LANGUAGES.map((l) => (
                    <label
                        key={l.code}
                        lang={l.bcp47}
                        className={classNames(styles.item, {[styles.selected]: l.code === language})}
                    >
                        <input
                            type="radio"
                            name="session-language"
                            value={l.code}
                            checked={l.code === language}
                            onChange={() => onLanguageChange(l.code)}
                        />
                        <span className={styles.flag} aria-hidden="true">{l.flag}</span>
                        <span className={styles.endonym}>{l.endonym}</span>
                    </label>
                ))}
            </fieldset>

            {/* Phone: pinned under the scrolling list, so Begin never falls below the fold
                (pages/session/spec.md § Layout notes). */}
            <div className={styles.pinned}>
                {language && voices.length > 0 && (
                    <div className={styles.voiceRow}>
                        <VoiceList language={language} voices={voices} voiceId={voiceId} onChange={onVoiceChange} />
                    </div>
                )}

                <button
                    type="button"
                    className={classNames(button.lit, button.large, styles.begin)}
                    disabled={!language || beginDisabled}
                    onClick={onBegin}
                >
                    {t.nav.beginSession}
                </button>
                {note && <p className={styles.note}>{note}</p>}
            </div>
        </div>
    )
}

interface LanguagePickerProps {
    currentLanguage: string | null
    onLanguageChange: (code: string) => void
    disabled?: boolean
}

/** Popover on desktop, sheet on phone: one markup, the stylesheet decides. */
export function LanguagePicker({currentLanguage, onLanguageChange, disabled}: LanguagePickerProps) {
    const {t} = useInterfaceStrings()
    const {open, setOpen, containerRef, triggerRef, popoverRef} = usePopover(disabled)
    const current = findLanguage(currentLanguage)

    return (
        <div ref={containerRef} className={styles.container}>
            <button
                ref={triggerRef}
                type="button"
                className={classNames(styles.trigger, {[styles.disabled]: disabled})}
                aria-label={t.session.changeLanguage}
                aria-haspopup="dialog"
                aria-expanded={open}
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
            >
                <span className={styles.flag} aria-hidden="true">
                    {current?.flag ?? <Icon id="qs-language" size={18} outline />}
                </span>
                <span className={styles.isoCode}>{current ? current.code.split('-')[0].toUpperCase() : ''}</span>
            </button>

            {open && (
                <div ref={popoverRef} className={styles.popover} role="dialog" aria-label={t.session.changeLanguage}>
                    <ul className={styles.rows}>
                        {LANGUAGES.map((l) => (
                            <li key={l.code}>
                                <button
                                    type="button"
                                    lang={l.bcp47}
                                    className={styles.row}
                                    aria-current={l.code === currentLanguage}
                                    onClick={() => { onLanguageChange(l.code); setOpen(false) }}
                                >
                                    <span className={styles.flag} aria-hidden="true">{l.flag}</span>
                                    <span>{l.endonym}</span>
                                    {l.code === currentLanguage && <Icon id="qs-check" size={14} outline className={styles.check} />}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}
